import React, { memo, useCallback } from 'react';
import { StyleProp, ViewStyle } from 'react-native';

import { CompositeNavigationProp, useNavigation } from '@react-navigation/native';

import { Box } from '@/components/lib/components';
import { BetrAnalytics } from '@/feature/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { useRaf } from '@/feature/invite-friends/hooks/use-raf';
import { Carousel, itemSeparatorWidth } from '@/feature/promohub/components/Carousel';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useAppResumeEffect } from '@/hooks/use-resume';
import { ArcadeLobbyTabNavigationProp } from '@/navigation/arcade/types';
import { PickemLobbyTabNavigationProp } from '@/navigation/pickem/types';
import { SweepsLobbyTabNavigationProp } from '@/navigation/sweeps/types';
import { isWeb } from '@/utils/constants-platform-specific';
import { getProductRoot } from '@/utils/product';

import { usePromotions } from '../hooks/use-promotions';
import { usePromotionsStore } from '../hooks/use-promotions-store';
import { Promotion } from '../types';
import { carouselItemWidth } from './Carousel';
import { PromoCard } from './PromoCard';
import { RAF_PROMO_CARD_WIDTH, RafPromoCard } from './RafPromoCard';
import { ViewAllPromoCard } from './ViewAllPromoCard';

// A navigation prop that works for all lobby stacks
type GenericLobbyPromoNavigationProp = CompositeNavigationProp<
    PickemLobbyTabNavigationProp,
    CompositeNavigationProp<ArcadeLobbyTabNavigationProp, SweepsLobbyTabNavigationProp>
>;

export const LobbyCarousel = memo(({ style }: { style?: StyleProp<ViewStyle> }) => {
    const { promotions, isLoading: isPromotionsLoading, refetch: refetchPromotions } = usePromotions();
    const navigation = useNavigation<GenericLobbyPromoNavigationProp>();
    const { data: rafConfig, refetch: refetchRaf, isPending: isRafLoading, isRafEnabled } = useRaf();

    const clearLocallyOptedInPromos = usePromotionsStore(state => state.actions.clearLocallyOptedIn);

    useAppResumeEffect(
        useCallback(() => {
            clearLocallyOptedInPromos();
            refetchPromotions();
            refetchRaf();
        }, [clearLocallyOptedInPromos, refetchPromotions, refetchRaf])
    );

    const isLoading = isPromotionsLoading || isRafLoading;

    const goToPromos = useCallback(() => {
        const state = navigation.getState();
        const hasLobbyPromosRoute = state?.routeNames?.some(route => route === 'LobbyPromohub');
        BetrAnalytics.trackProductEvent(AnalyticsEvent.VIEW_ALL_PROMOS_FROM_LOBBY_CAROUSEL);
        // if the PromoHub is available in the current lobby stack
        // we navigate there directly -> this is the case for Arcade and Pickem
        if (hasLobbyPromosRoute) {
            navigation.navigate('LobbyPromohub');
            return;
        } else {
            // otherwise we navigate to the Promohub bottom tab
            const product = useJurisdictionStore.getState().product;
            const productRoot = getProductRoot(product);
            navigation.navigate(productRoot, {
                screen: 'PromohubStack',
                params: {
                    screen: 'Promohub',
                    params: { mode: 'tabs' },
                },
            });
        }
    }, [navigation]);

    const renderCarouselItem = useCallback((item: Promotion) => {
        return <PromoCard key={item.uid} promo={item} mode={'lobby'} cardWidth={carouselItemWidth} />;
    }, []);

    const renderViewAllButton = useCallback(() => {
        return <ViewAllPromoCard onPress={goToPromos} />;
    }, [goToPromos]);

    const renderRAFCard = useCallback(() => {
        return <RafPromoCard isLoading={isLoading} rafConfig={rafConfig} />;
    }, [isLoading, rafConfig]);

    if (!isLoading && !promotions.length) {
        return null;
    }

    return (
        <Box style={style}>
            <Carousel<Promotion>
                data={promotions}
                isLoading={isLoading}
                showCarouselArrows={isWeb}
                renderCarouselItem={renderCarouselItem}
                renderFooter={renderViewAllButton}
                renderHeader={isRafEnabled ? renderRAFCard : undefined}
                headerWidth={isRafEnabled ? RAF_PROMO_CARD_WIDTH + itemSeparatorWidth : undefined}
            />
        </Box>
    );
});
